<template>
  <PModal
    :open="isOpen"
    :title="currentDialog?.title"
    :description="currentDialog?.body"
    :dismissible="!currentDialog?.lock"
    :close="false"
    :content="{ onCloseAutoFocus }"
    :ui="{
      overlay: 'z-60 backdrop-blur-xs backdrop-brightness-50',
      content: 'z-60 max-w-[600px] select-none',
      description: 'whitespace-pre-line',
      footer: 'flex-wrap gap-2'
    }"
    class="p-modal"
    scrollable
    @update:open="onDialogOpenChange"
  >
    <template v-if="currentDialog?.template" #body>
      <component :is="currentDialog.template" v-model="currentDialog.templateProps.modelValue" />
    </template>

    <template v-if="currentDialog && currentDialog.type !== DialogTypes.NO_BUTTON" #footer>
      <PButton
        v-if="confirmButtonText"
        :class="currentDialog.classConfirm"
        :disabled="waitTimeLeftSec > 0"
        class="min-w-14"
        @click="onUserConfirm"
      >
        <span>{{ confirmButtonText }}</span>
        <span v-if="waitTimeLeftSec > 0" class="ml-1">({{ waitTimeLeftSec }})</span>
      </PButton>
      <PButton
        v-if="currentDialog.type === DialogTypes.CONFIRM_DECLINE"
        color="error"
        @click="onUserDecline"
      >
        {{ declineButtonText }}
      </PButton>
      <PButton
        v-if="[DialogTypes.CONFIRM, DialogTypes.CONFIRM_DECLINE].includes(currentDialog.type)"
        variant="outline"
        @click="onUserCancel"
      >
        {{ cancelButtonText }}
      </PButton>
      <PButton v-if="currentDialog.type === DialogTypes.CUSTOM" @click="onUserCustom">
        {{ customButtonText }}
      </PButton>
    </template>
  </PModal>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue"
import PModal from "./PModal.vue"
import { storeToRefs } from "pinia"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import PButton from "@/components/elements/PButton.vue"

const dialogStore = useDialogStore()
const { currentDialog } = storeToRefs(dialogStore)

let countdownTimer: ReturnType<typeof setInterval> | null = null

const defaultWaitTimeSec = 1

const waitTimeLeftSec = ref(0)

const isOpen = computed(() => {
  return !!currentDialog.value
})

const defaultConfirmButtonTextMap: Partial<Record<DialogTypes, string | null>> = {
  [DialogTypes.OK]: "Oké",
  [DialogTypes.CONFIRM]: "Bevestigen",
  [DialogTypes.NO_BUTTON]: null,
  [DialogTypes.CUSTOM]: null
}

const confirmButtonText = computed(() => {
  if (currentDialog.value) {
    if (currentDialog.value.textConfirm) {
      return currentDialog.value.textConfirm
    }
    return defaultConfirmButtonTextMap[currentDialog.value.type]
  }
  return null
})

const declineButtonText = computed(() => {
  if (currentDialog.value?.textDecline) {
    return currentDialog.value.textDecline
  }
  return "Afwijzen"
})

const cancelButtonText = computed(() => {
  if (currentDialog.value?.textCancel) {
    return currentDialog.value.textCancel
  }
  return "Annuleren"
})

const customButtonText = computed(() => {
  if (currentDialog.value?.textCustom) {
    return currentDialog.value.textCustom
  }
  return "Annuleren"
})

function onUserConfirm() {
  if (currentDialog.value?.onConfirm) {
    currentDialog.value.onConfirm()
  }
  currentDialog.value = null
}

function onUserDecline() {
  if (currentDialog.value?.onDecline) {
    currentDialog.value.onDecline()
  }
  currentDialog.value = null
}

function onUserCancel() {
  if (currentDialog.value?.onCancel) {
    currentDialog.value.onCancel()
  }
  currentDialog.value = null
}

function onUserCustom() {
  if (currentDialog.value?.onCustom) {
    currentDialog.value.onCustom()
  }
}

function onUserEscape() {
  if (currentDialog.value?.lock) {
    return
  }
  onUserCancel()
}

function onDialogOpenChange(open: boolean) {
  if (!open) {
    onUserEscape()
  }
}

let storedActiveElement: HTMLElement | null = null
let restoreOriginalFocus = true

function onCloseAutoFocus(event: Event) {
  event.preventDefault()
  if (restoreOriginalFocus) {
    storedActiveElement?.focus()
  }
  storedActiveElement = null
}

watch(
  currentDialog,
  (newVal, oldVal) => {
    if (newVal && !oldVal) {
      storedActiveElement = document.activeElement as HTMLElement
    } else if (!newVal && oldVal) {
      restoreOriginalFocus = oldVal.restoreOriginalFocus !== false
    }

    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
    waitTimeLeftSec.value = newVal?.mandatedWaitTimeSec ?? defaultWaitTimeSec
    if (newVal && waitTimeLeftSec.value > 0) {
      countdownTimer = setInterval(() => {
        waitTimeLeftSec.value--
        if (countdownTimer && waitTimeLeftSec.value <= 0) {
          clearInterval(countdownTimer)
          countdownTimer = null
        }
      }, 1000)
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
})
</script>
